import {
  faBuildingUser,
  faCloudArrowUp,
  faCloudCheck,
  faPhoneArrowUp
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'
import { parsePhoneNumberFromString } from 'libphonenumber-js'
import type { ReactElement, ReactNode } from 'react'
import { Shimmer } from '~/components/Global/Elements/Shimmer/Shimmer'
import { Tooltip } from '~/components/Global/Elements/Tooltip/Tooltip'
import {
  Button,
  ButtonVariation
} from '~/components/Global/Interactions/Button'
import { ToastType } from '~/components/Global/Interactions/Toast/ToastType'
import {
  applicantModalActions,
  applicantModalContactBadge,
  applicantModalHeader,
  applicantModalName,
  applicantModalNameLink,
  applicantModalPicture,
  applicantModalPictureWrapper,
  applicantModalRole,
  applicantModalTitle
} from '~/components/Scopes/Applicants/ApplicantModal/ApplicantModal.css'
import { useTempoSyncApplicant } from '~/domains/accounting/tempo/mutations/useTempoSyncApplicant'
import { useApplicantTempoStatus } from '~/domains/accounting/tempo/queries/useApplicantTempoStatus'
import type { ApplicantContactData } from '~/domains/applicants/types'
import { useConfig } from '~/domains/configuration/queries/config'
import type { TaxonomyItem } from '~/domains/taxonomies/types'
import { toast } from '~/utils'
import { ApplicantAvatar } from '../ApplicantAvatar/ApplicantAvatar'
import { ApplicantStatusUpdater } from '../ApplicantStatusUpdater'

export type ApplicantModalHeaderProps = {
  fullName: string
  profilePictureUrl?: string
  isLoading?: boolean
  phone?: string
  role?: ReactNode
  isProfilePictureLoading?: boolean
  applicantId?: number
  applicantStatus?: TaxonomyItem<'statuses'> | null
  contact?: ApplicantContactData
  children?: ReactNode
}

export const ApplicantModalHeader = ({
  fullName,
  profilePictureUrl,
  isLoading,
  phone,
  role,
  isProfilePictureLoading,
  applicantId,
  applicantStatus,
  contact,
  children
}: ApplicantModalHeaderProps): ReactElement => {
  const { data: legacyUrlBase } = useConfig('organization.legacy_url')
  const { data: tempoStatus } = useApplicantTempoStatus(applicantId)
  const tempoSync = useTempoSyncApplicant()

  const legacyUrl =
    applicantId && !isLoading && legacyUrlBase
      ? `${legacyUrlBase}/admin/candidats/edit/${applicantId}`
      : undefined

  return (
    <>
      {children}
      <div className={applicantModalPictureWrapper}>
        <ApplicantAvatar
          applicantId={applicantId}
          fullName={fullName}
          profilePictureUrl={
            profilePictureUrl &&
            profilePictureUrl.indexOf('/admin') !== -1 &&
            legacyUrlBase
              ? `${legacyUrlBase}${profilePictureUrl}`
              : profilePictureUrl
          }
          pictureClassName={applicantModalPicture}
          size={50}
          isLoading={isLoading}
          isProfilePictureLoading={isProfilePictureLoading}
          source="applicant_modal"
        />
      </div>
      <div className={applicantModalHeader}>
        <div className={applicantModalTitle}>
          <h3 className={clsx(applicantModalName)}>
            {isLoading && !fullName ? (
              <Shimmer height={20} width={100} />
            ) : legacyUrl ? (
              <a
                href={legacyUrl}
                target="_blank"
                className={clsx(applicantModalName, applicantModalNameLink)}
                rel="noopener noreferrer"
              >
                {fullName}
              </a>
            ) : (
              <span className={applicantModalName}>{fullName}</span>
            )}
          </h3>
          <div className={applicantModalActions}>
            {tempoStatus !== undefined &&
              !isLoading &&
              (tempoStatus.canSync ? (
                <Tooltip
                  asChild
                  text={
                    tempoStatus.synced
                      ? 'Resynchroniser vers Tempo'
                      : 'Synchroniser vers Tempo'
                  }
                >
                  <Button
                    narrow
                    variation={ButtonVariation.Neutral}
                    buttonSize="extraSmall"
                    disabled={tempoSync.isPending}
                    onClick={() => {
                      if (!applicantId) return
                      tempoSync.mutate(applicantId, {
                        onSuccess: () =>
                          toast(
                            'Synchronisation Tempo réussie',
                            '',
                            ToastType.Success
                          ),
                        onError: () =>
                          toast(
                            'Échec de la synchronisation Tempo',
                            '',
                            ToastType.Danger
                          )
                      })
                    }}
                  >
                    <FontAwesomeIcon
                      icon={
                        !tempoSync.isPending && tempoStatus.synced
                          ? faCloudCheck
                          : faCloudArrowUp
                      }
                    />
                  </Button>
                </Tooltip>
              ) : tempoStatus.synced ? (
                <Tooltip asChild text="Synchronisé avec Tempo">
                  <Button
                    narrow
                    variation={ButtonVariation.Neutral}
                    buttonSize="extraSmall"
                    disabled
                  >
                    <FontAwesomeIcon icon={faCloudCheck} />
                  </Button>
                </Tooltip>
              ) : null)}
            {phone && !isLoading && (
              <Button
                href={
                  parsePhoneNumberFromString(phone ?? '', 'FR')?.format(
                    'RFC3966'
                  ) ?? `tel:${encodeURIComponent(phone.replace(/[^\d+]/g, ''))}`
                }
                data-applicant-id={applicantId}
                narrow
                variation={ButtonVariation.Neutral}
                buttonSize="extraSmall"
              >
                <FontAwesomeIcon icon={faPhoneArrowUp} />
              </Button>
            )}
            {applicantId && applicantStatus && (
              <ApplicantStatusUpdater
                applicantId={applicantId}
                initialStatus={applicantStatus}
              />
            )}
          </div>
        </div>
        <h4 className={applicantModalRole}>
          {role}
          {contact && (
            <a
              href={
                legacyUrlBase
                  ? `${legacyUrlBase}/admin/entreprises/edit/${contact.company.id}`
                  : undefined
              }
              target="_blank"
              rel="noopener noreferrer"
              className={applicantModalContactBadge}
            >
              <FontAwesomeIcon icon={faBuildingUser} />
              {contact.company.name}
            </a>
          )}
        </h4>
      </div>
    </>
  )
}
